import { Stack } from "@fluentui/react";
import { useCallback, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { Banner } from "./types/BannersOffers.types";
import { PlanOfferItem } from "./types/PlansOffers.types";

import undrawDataProcessing from "@/assets/images/undrawDataProcessing.svg";
import {
  PermissionsAction,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import { BannersOffers } from "@/components/Offers/BannersOffers";
import { PlansOffers } from "@/components/Offers/PlansOffers";
import { ProductsOffers } from "@/components/Offers/ProductOffers";
import { fadeIn } from "@/components/Shared/FadeIn";
import { Loader } from "@/components/Shared/Loader";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import {
  useGetDashboardProducts,
  useGetProfile
} from "@/core/libs/api/react-query/index";
import { useAuth } from "@/core/libs/use-auth";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { listRequest as listProductsOffersRequest } from "@/modules/Offers/actions/productsOffers";

const PlansWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  width: 100%;
  justify-content: center;
  align-items: stretch;

  > * {
    flex: 0 0 350px;
    height: auto;
  }
`;

function Offers(): JSX.Element {
  const isInitialRender = useRef(true);
  const userProfile = useGetProfile();
  const { selectedBU } = useSelectedBu();
  const permissions = userProfile.data?.data?.role.permissions;
  const { isAuthenticated } = useAuth();

  const { data: listOfBanners, isFetching: isLoadingBannersList } = useGet<
    Array<Banner>
  >({
    queryToken: `banners-${selectedBU?.id}`,
    url: "dashboards/banners?query=true",
    params: {
      businessActionId: selectedBU?.id,
      limit: 10,
      page: 1
    },
    enabled: isAuthenticated && Boolean(selectedBU?.id)
  });

  const { data: listOfTags, isFetching: isLoadingTagOffersList } =
    useGet<PlanOfferItem>({
      queryToken: `tags-${selectedBU?.id}`,
      url: "dashboards/tags?query=true",
      params: { businessActionId: selectedBU?.id },
      enabled: isAuthenticated && Boolean(selectedBU?.id)
    });

  const { data: listOfPlansOffers, isFetching: isLoadingPlansOffersList } =
    useGet<Array<PlanOfferItem>>({
      queryToken: `plans-${selectedBU?.id}`,
      url: "dashboards/plans?query=true",
      params: { businessActionId: selectedBU?.id },
      enabled: isAuthenticated && Boolean(selectedBU?.id)
    });

  const { data: listOfPabxOffers, isFetching: isLoadingPabxOffersList } =
    useGet<Array<PlanOfferItem>>({
      queryToken: `pabx-${selectedBU?.id}`,
      url: "dashboards/pabx",
      params: { businessActionId: selectedBU?.id },
      enabled: isAuthenticated && Boolean(selectedBU?.id)
    });

  const { data: listOfEnergyOffers, isFetching: isLoadingEnergyOffersList } =
    useGet<Array<PlanOfferItem>>({
      queryToken: `energy-${selectedBU?.id}`,
      url: "dashboards/energy",
      params: { businessActionId: selectedBU?.id },
      enabled: isAuthenticated && Boolean(selectedBU?.id)
    });

  const isAnySpecialOfferLoading =
    isLoadingTagOffersList ||
    isLoadingPabxOffersList ||
    isLoadingEnergyOffersList;

  const hasTagOffers = listOfTags?.plans?.length > 0;
  const hasPabxOffers = listOfPabxOffers?.length > 0;
  const hasEnergyOffers = listOfEnergyOffers?.length > 0;
  const hasSpecialOffers = hasTagOffers || hasPabxOffers || hasEnergyOffers;

  const {
    productsOffersList,
    productsBrands,
    productsCarriers,
    productsCategories,
    isLoadingProductsOffersList
  } = useGetDashboardProducts();

  const canReadDashboardTags = permissions[PermissionsScope.POS]?.[
    PermissionsPosModule.DASHBOARD_TAGS
  ]?.some(
    (dashboardTagsAction: PermissionsAction) =>
      dashboardTagsAction === PermissionsAction.ALL ||
      dashboardTagsAction === PermissionsAction.READ
  );

  const dispatch = useDispatch();

  const requestListOfProductOffers = useCallback(
    (filters?) => {
      dispatch(
        listProductsOffersRequest({
          businessActionId: selectedBU?.id,
          ...filters
        })
      );
    },
    [dispatch, selectedBU]
  );

  useEffect(() => {
    if (isInitialRender.current && productsOffersList) {
      isInitialRender.current = false;
    }
  }, [productsOffersList]);

  useEffect(() => {
    if (!isInitialRender.current) {
      isInitialRender.current = true;
    }
  }, [selectedBU]);

  const hasBanners = listOfBanners?.length > 0;
  const hasPlansOffers = listOfPlansOffers?.length > 0;
  const hasProductOffers = productsOffersList?.products?.items?.length > 0;

  const hasNoBanners = !isLoadingBannersList && !hasBanners;
  const hasNoPlansOffers = !isLoadingPlansOffersList && !hasPlansOffers;
  const hasNoSpecialOffers = !isAnySpecialOfferLoading && !hasSpecialOffers;
  const hasNoProductOffers = !isLoadingProductsOffersList && !hasProductOffers;

  const shouldShowEmptyState =
    hasNoBanners &&
    hasNoPlansOffers &&
    hasNoSpecialOffers &&
    hasNoProductOffers;

  return (
    <Stack
      styles={{ root: { width: "100%", height: "100%", minHeight: "100%" } }}
    >
      <Helmet>
        <title>Pacotes de Benefícios - Libertas</title>
      </Helmet>
      <Stack
        tokens={{ childrenGap: 15 }}
        styles={{ root: { minHeight: "100%" } }}
      >
        {isLoadingBannersList ? (
          <Stack.Item
            shrink={0}
            styles={{ root: { position: "relative", height: 200 } }}
          >
            <Loader customMessage="Carregando banners" />
          </Stack.Item>
        ) : (
          hasBanners && (
            <Stack styles={fadeIn}>
              <BannersOffers banners={listOfBanners} />
            </Stack>
          )
        )}

        {isAnySpecialOfferLoading && canReadDashboardTags ? (
          <Stack.Item shrink={0} styles={{ root: { height: 200 } }}>
            <Loader customMessage="Carregando pacotes de benefícios Tag, PABX e Energia" />
          </Stack.Item>
        ) : (
          hasSpecialOffers && (
            <>
              <h1 className="ms-fontWeight-semibold ms-fontSize-24">
                Pacote de Benefícios Tag e Conectividade Fixa
              </h1>

              <Stack
                horizontal
                horizontalAlign="center"
                wrap
                tokens={{ childrenGap: 50 }}
              >
                {hasTagOffers && (
                  <Stack styles={fadeIn}>
                    <PlansOffers
                      plans={[listOfTags]}
                      isLoadingList={isLoadingTagOffersList}
                      formType="TAGS"
                    />
                  </Stack>
                )}
                {hasPabxOffers && (
                  <Stack styles={fadeIn}>
                    <PlansOffers
                      plans={listOfPabxOffers}
                      isLoadingList={isLoadingPabxOffersList}
                      formType={"LINES"}
                    />
                  </Stack>
                )}
                {hasEnergyOffers && (
                  <Stack styles={fadeIn}>
                    <PlansOffers
                      plans={listOfEnergyOffers}
                      isLoadingList={isLoadingEnergyOffersList}
                      formType={"ENERGY"}
                    />
                  </Stack>
                )}
              </Stack>
            </>
          )
        )}

        {isLoadingPlansOffersList ? (
          <Stack.Item
            shrink={0}
            styles={{
              root: {
                position: "relative",
                height: 300
              }
            }}
          >
            <Loader customMessage="Carregando pacote de benefícios somente chip" />
          </Stack.Item>
        ) : (
          hasPlansOffers && (
            <Stack.Item styles={{ root: { paddingBottom: 100 } }}>
              <h1 className="ms-fontWeight-semibold ms-fontSize-24">
                Pacote de Benefícios Móvel
              </h1>
              <Stack styles={fadeIn}>
                <PlansWrapper>
                  <PlansOffers
                    plans={listOfPlansOffers}
                    isLoadingList={isLoadingPlansOffersList}
                    formType={"LINES"}
                  />
                </PlansWrapper>
              </Stack>
            </Stack.Item>
          )
        )}

        {isLoadingProductsOffersList && isInitialRender.current ? (
          <Stack.Item
            shrink={0}
            styles={{
              root: { position: "relative", height: 400 }
            }}
          >
            <Loader customMessage="Carregando combos" />
          </Stack.Item>
        ) : (
          hasProductOffers && (
            <Stack styles={fadeIn}>
              <ProductsOffers
                products={productsOffersList?.products}
                productsBrands={productsBrands}
                productsCarriers={productsCarriers}
                productsCategories={productsCategories}
                isLoadingList={isLoadingProductsOffersList}
                requestListOfProductOffers={requestListOfProductOffers}
              />
            </Stack>
          )
        )}

        {shouldShowEmptyState && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{ root: { height: "100%" } }}
          >
            <img
              src={undrawDataProcessing}
              style={{
                userSelect: "none",
                width: "50vw",
                height: "50vh"
              }}
              draggable="false"
              alt="Nenhum benefício encontrado"
              title="Sem benefícios para essa unidade de negócio."
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default Offers;
